import { useTranslation } from "react-i18next";
import { PackageOpen, Plus, SearchX } from "lucide-react";
import type { Product } from "@nh/shared";
import { Alert, Button } from "@/shared/ui/core";
import { EmptyState, SkeletonTable } from "@/shared/ui/ui";
import { ProductsTable } from "./ProductsTable";
import { useApiErrorMessage } from "../useApiErrorMessage";

export interface ProductsListBodyProps {
  products: readonly Product[];
  loading: boolean;
  error: Error | null;
  /** `true` si hay búsqueda o filtros puestos: cambia el estado de vacío. */
  filtered: boolean;
  onRetry: () => void;
  onCreate: () => void;
  onResetFilters: () => void;
  onEdit: (product: Product) => void;
  onArchive: (product: Product) => void;
  archivingId?: string | null;
}

/**
 * El cuerpo del listado: esqueleto, error, vacío o tabla.
 *
 * Los cuatro estados están AQUÍ y no repartidos por la página porque son
 * excluyentes entre sí, y cuando se escriben sueltos siempre acaba
 * habiendo una combinación que pinta dos a la vez —el esqueleto encima
 * de un error, típicamente—.
 *
 * "Vacío" son dos estados distintos, y confundirlos es el clásico:
 * "todavía no hay productos" invita a crear el primero; "tu búsqueda no
 * encontró nada" invita a limpiar el filtro. Enseñar el primero cuando
 * pasa el segundo hace pensar que el catálogo se borró.
 */
export function ProductsListBody({
  products,
  loading,
  error,
  filtered,
  onRetry,
  onCreate,
  onResetFilters,
  onEdit,
  onArchive,
  archivingId = null,
}: ProductsListBodyProps) {
  const { t } = useTranslation();
  const errorMessage = useApiErrorMessage(error);

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="error" title={t("No se pudo cargar el catálogo")}>
          <p>{errorMessage}</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
            {t("Reintentar")}
          </Button>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-5" aria-busy="true" aria-live="polite">
        <span className="sr-only">{t("Cargando el catálogo…")}</span>
        <SkeletonTable rows={6} columns={6} />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="p-4">
        {filtered ? (
          <EmptyState
            icon={<SearchX className="h-5 w-5 text-ds-ink-3" />}
            title={t("Sin resultados")}
            description={t("Ningún producto coincide con lo que buscas.")}
            action={
              <Button variant="secondary" size="sm" onClick={onResetFilters}>
                {t("Limpiar filtros")}
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<PackageOpen className="h-5 w-5 text-ds-ink-3" />}
            title={t("Todavía no hay productos")}
            description={t("Crea el primero para empezar el catálogo.")}
            action={
              <Button size="sm" leftIcon={<Plus />} onClick={onCreate}>
                {t("Nuevo producto")}
              </Button>
            }
          />
        )}
      </div>
    );
  }

  return (
    <ProductsTable
      products={products}
      onEdit={onEdit}
      onArchive={onArchive}
      archivingId={archivingId}
    />
  );
}
